import { Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { MultiSelectAutocomplete } from "components/common/MultiSelectAutocomplete";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import {
  CameraCapability,
  ScrapEyeRecordingPosition,
  useListAvailableCameraAnglesQuery,
  useListScrapEyeCamerasQuery,
} from "src/store/api/generatedApi";

export type VideoSourceMode = "angle" | "camera" | "angle_group";

export const doesSourceHaveFeeds = (source: VideoSource) => {
  switch (source.mode) {
    case "angle":
      return source.angles.length > 0;
    case "angle_group":
      return source.videoPlayerGroups.length > 0;
    case "camera":
      return source.cameras.length > 0;
    default:
      return false;
  }
};

export type VideoSource =
  | {
      mode: "angle";
      angles: readonly {
        id: number;
        location_name: string;
        name: string;
      }[];
      deviceId?: number;
    }
  | {
      mode: "angle_group";
      videoPlayerGroups: readonly {
        groupId: number;
        location_name: string;
      }[];
    }
  | {
      mode: "camera";
      cameras: readonly { deviceId: number; physicalId: string }[];
    };

type VideoSourceSelectorProps = Readonly<{
  deviceId: number;
  mode: VideoSourceMode;
  onModeChange: (mode: VideoSourceMode) => void;
  selectedAngleIds: readonly number[];
  onAngleIdsChange: (ids: readonly number[]) => void;
  selectedCameraPhysicalIds: readonly string[];
  onCameraPhysicalIdsChange: (ids: readonly string[]) => void;
  atTime?: string;
}>;

export const VideoSourceSelector = ({
  deviceId,
  mode,
  onModeChange,
  selectedAngleIds,
  onAngleIdsChange,
  selectedCameraPhysicalIds,
  onCameraPhysicalIdsChange,
  atTime,
}: VideoSourceSelectorProps) => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();

  const { data: cameraAnglesData } = useListAvailableCameraAnglesQuery({
    tenantName,
    deviceIds: [deviceId],
    atTime,
  });

  const { data: camerasData } = useListScrapEyeCamerasQuery({
    tenantName,
    deviceId,
  });

  const availableAngles = cameraAnglesData?.items ?? [];
  const availableCameras = camerasData?.cameras ?? [];

  const handleModeChange = (
    _: React.MouseEvent<HTMLElement>,
    newMode: VideoSourceMode | null
  ) => {
    if (newMode !== null) {
      onModeChange(newMode);
    }
  };

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <ToggleButtonGroup
        value={mode}
        exclusive
        onChange={handleModeChange}
        size="small"
      >
        <ToggleButton value="angle">{t("cameraAngle")}</ToggleButton>
        <ToggleButton value="camera">{t("camera")}</ToggleButton>
      </ToggleButtonGroup>

      {mode === "angle" ? (
        <AngleSelectorInner
          selectedAngles={selectedAngleIds}
          onAnglesChange={onAngleIdsChange}
          availableAngles={availableAngles}
        />
      ) : (
        <CameraSelectorInner
          selectedCameras={selectedCameraPhysicalIds}
          onCamerasChange={onCameraPhysicalIdsChange}
          availableCameras={availableCameras}
        />
      )}
    </Stack>
  );
};

const AngleSelectorInner = ({
  selectedAngles,
  onAnglesChange,
  availableAngles,
}: {
  selectedAngles: readonly number[];
  onAnglesChange: (angles: readonly number[]) => void;
  availableAngles: readonly ScrapEyeRecordingPosition[];
}) => {
  const { t } = useTenantTranslation();

  if (availableAngles.length === 0) {
    return null;
  }

  return (
    <MultiSelectAutocomplete
      options={[...availableAngles]}
      selectedIds={selectedAngles}
      onChange={onAnglesChange}
      getOptionLabel={(angle: ScrapEyeRecordingPosition) =>
        `${angle.location_name} - ${angle.name}`
      }
      filterOption={(option: ScrapEyeRecordingPosition, inputValue: string) => {
        const lowerInput = inputValue.toLowerCase();
        return (
          option.location_name.toLowerCase().includes(lowerInput) ||
          option.name.toLowerCase().includes(lowerInput)
        );
      }}
      label={t("cameraAngle")}
      placeholder={t("selectCameraAngles")}
      chipPlacement="below"
    />
  );
};

interface CameraOption {
  id: string;
  physical_id: string;
}

const CameraSelectorInner = ({
  selectedCameras,
  onCamerasChange,
  availableCameras,
}: {
  selectedCameras: readonly string[];
  onCamerasChange: (cameras: readonly string[]) => void;
  availableCameras: readonly CameraCapability[];
}) => {
  const { t } = useTenantTranslation();

  if (availableCameras.length === 0) {
    return null;
  }

  const cameraOptions: CameraOption[] = availableCameras.map((camera) => ({
    id: camera.physical_id,
    physical_id: camera.physical_id,
  }));

  return (
    <MultiSelectAutocomplete
      options={cameraOptions}
      selectedIds={selectedCameras}
      onChange={onCamerasChange}
      getOptionLabel={(camera: CameraOption) => camera.physical_id}
      filterOption={(option: CameraOption, inputValue: string) => {
        const lowerInput = inputValue.toLowerCase();
        return option.physical_id.toLowerCase().includes(lowerInput);
      }}
      label={t("camera")}
      placeholder={t("selectCameras")}
      chipPlacement="below"
    />
  );
};

export const useVideoSources = ({
  deviceId,
  mode,
  selectedAngleIds,
  selectedCameraPhysicalIds,
  atTime,
}: {
  deviceId: number;
  mode: VideoSourceMode;
  selectedAngleIds: readonly number[];
  selectedCameraPhysicalIds: readonly string[];
  atTime?: string;
}): VideoSource => {
  const tenantName = useTenant();

  const { data: cameraAnglesData } = useListAvailableCameraAnglesQuery({
    tenantName,
    deviceIds: [deviceId],
    atTime,
  });

  const availableAngles = cameraAnglesData?.items ?? [];

  if (mode === "angle") {
    const angles = availableAngles.filter((a) =>
      selectedAngleIds.includes(a.id)
    );
    return { mode: "angle", angles, deviceId };
  }

  const cameras = selectedCameraPhysicalIds.map((physicalId) => ({
    deviceId,
    physicalId,
  }));
  return { mode: "camera", cameras };
};
